/** Pick a workout template to assign.
 *
 *  The four toggles are exactly the ones `changeProgram` accepts, and the reason the wizard assigns
 *  the program here rather than through `createTrainee`'s `templateId`: that path hard-codes
 *  `ProgramSettings` to null, so all four would silently take defaults. */

import { Box, Checkbox, CircularProgress, FormControlLabel, TextField, Typography } from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import type { AdminNewTemplate } from "../../../../api/generated";
import { t } from "../../../dashboard/coach/coachTokens";
import FilterDropdown from "../../../ui/FilterDropdown";
import { experienceLevels } from "../../../../enums/ExperienceLevels";
import { bodyParts } from "../../../../enums/BodyParts";
import StepShell from "../StepShell";
import { fieldSx } from "../wizardStyles";
import type { WorkoutFilters, WorkoutValues } from "../wizardReducer";

/* The same three filters the templates library offers, in the same words — a coach who knows
   `/app/templates` should not have to learn a second vocabulary here. Gender is deliberately NOT
   among them: it is pinned to the client's own, exactly as `ChnageProgramDialog` does. */
const WORKOUT_COUNT_OPTIONS = [
  { value: 0, label: "הכול" },
  ...[1, 2, 3, 4, 5, 6, 7].map(n => ({ value: n, label: `${n} ${n === 1 ? "אימון" : "אימונים"}` })),
];

const EXPERIENCE_OPTIONS = [
  { value: "ALL", label: "הכול" },
  ...experienceLevels.map(level => ({ value: level.value, label: level.name })),
];

const BODY_PART_OPTIONS = [
  { value: "ALL", label: "הכול" },
  ...bodyParts.map(part => ({ value: part.value, label: part.name })),
];

const SETTINGS: Array<{ key: keyof WorkoutValues; label: string; hint: string }> = [
  {
    key: "includeRmWeek",
    label: "שבוע בדיקת מקסימום (RM)",
    hint: "מוסיף שבוע מבחן בתחילת התוכנית",
  },
  {
    key: "automaticProgression",
    label: "התקדמות אוטומטית",
    hint: "העלאת עומסים אוטומטית בין שבועות",
  },
  {
    key: "useProgressionAlgorithm",
    label: "אלגוריתם התקדמות",
    hint: "חישוב העומס לפי הביצועים בפועל",
  },
  { key: "showRir", label: "הצגת RIR למתאמן", hint: "חזרות במילואים מוצגות באפליקציה" },
];

/** `enums/ExperienceLevels.ts` is the picker's list and omits `BEGINNER`, which templates can
 *  still carry. This map is exhaustive over the DTO's union so a level never renders blank. */
const EXPERIENCE_LABELS: Record<NonNullable<AdminNewTemplate["experienceLevel"]>, string> = {
  BEGINNER: "מתחיל",
  BEGINNER_ADVANCE: "מתחיל מתקדם",
  MEDIUM: "בינוני",
  ADVANCE: "מקצוען",
};

const templateLabel = (template: AdminNewTemplate) =>
  template.nameHeb || template.name || `תבנית ${template.id}`;

interface WorkoutStepProps {
  values: WorkoutValues;
  error: string | null;
  templates: AdminNewTemplate[];
  loading: boolean;
  /** Applied server-side — the list is re-fetched, not narrowed in the browser, so a template
   *  past the first page is still reachable. */
  filters: WorkoutFilters;
  onFiltersChange: (filters: Partial<WorkoutFilters>) => void;
  onChange: (values: Partial<WorkoutValues>) => void;
}

const WorkoutStep = ({
  values,
  error,
  templates,
  loading,
  filters,
  onFiltersChange,
  onChange,
}: WorkoutStepProps) => {
  const matches = templates;

  return (
    <StepShell
      title="תוכנית אימון"
      description="בחירת תבנית יוצרת למתאמן תוכנית מלאה. אפשר לערוך בלוקים ושבועות אחר כך מעמוד הלקוח."
      error={error}
    >
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", color: t.t2, fontSize: "13px" }}>
          <CircularProgress size={16} sx={{ color: t.green }} />
          טוען תבניות אימון…
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
            <TextField
              size="small"
              value={filters.keyword}
              placeholder="חפש תבנית..."
              inputProps={{ "aria-label": "חיפוש תבנית אימון" }}
              sx={{ ...(fieldSx as object), flex: "1 1 220px", minWidth: "180px" }}
              onChange={event => onFiltersChange({ keyword: event.target.value })}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: t.t3, fontSize: 19, marginInlineEnd: "6px" }} />
                ),
              }}
            />
            <FilterDropdown
              label="אימונים בשבוע"
              value={filters.numberOfWorkouts}
              onChange={(value: number) => onFiltersChange({ numberOfWorkouts: value })}
              options={WORKOUT_COUNT_OPTIONS}
              minWidth={140}
            />
            <FilterDropdown
              label="רמה"
              value={filters.experienceLevel}
              onChange={(value: string) => onFiltersChange({ experienceLevel: value })}
              options={EXPERIENCE_OPTIONS}
              minWidth={110}
            />
            <FilterDropdown
              label="דגש ראשי"
              value={filters.bodyPart}
              onChange={(value: string) => onFiltersChange({ bodyPart: value })}
              options={BODY_PART_OPTIONS}
              minWidth={120}
            />
          </Box>

          <Box
            role="radiogroup"
            aria-label="תבניות אימון"
            sx={{
              marginTop: "14px",
              maxHeight: "260px",
              overflowY: "auto",
              border: `1px solid ${t.border}`,
              borderRadius: t.rs,
            }}
          >
            {matches.length === 0 && (
              <Box sx={{ padding: "16px", fontSize: "13px", color: t.t3 }}>
                אין תבנית שתואמת את הסינון
              </Box>
            )}
            {matches.map(template => {
              const selected = values.templateId === template.id;
              return (
                <Box
                  key={template.id}
                  component="button"
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => onChange({ templateId: template.id ?? null })}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    width: "100%",
                    padding: "11px 14px",
                    border: "none",
                    borderBottom: `1px solid ${t.borderLight}`,
                    backgroundColor: selected ? t.greenLight : t.white,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    textAlign: "start",
                    "&:last-of-type": { borderBottom: "none" },
                    "&:hover": { backgroundColor: selected ? t.greenLight : t.bg },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        fontSize: "13.5px",
                        fontWeight: selected ? 700 : 600,
                        color: selected ? t.greenText : t.text,
                      }}
                    >
                      {templateLabel(template)}
                    </Box>
                    <Box sx={{ fontSize: "11.5px", color: t.t3, marginTop: "2px" }}>
                      {[
                        template.numOfWorkouts ? `${template.numOfWorkouts} אימונים בשבוע` : null,
                        template.numOfWeeks ? `${template.numOfWeeks} שבועות` : null,
                        template.experienceLevel
                          ? EXPERIENCE_LABELS[template.experienceLevel]
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ marginTop: "22px" }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 700, color: t.text, marginBottom: "8px" }}>
              הגדרות התוכנית
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "4px 20px" }}>
              {SETTINGS.map(setting => (
                <FormControlLabel
                  key={setting.key}
                  control={
                    <Checkbox
                      size="small"
                      checked={Boolean(values[setting.key])}
                      onChange={event => onChange({ [setting.key]: event.target.checked })}
                      sx={{ color: t.t3, "&.Mui-checked": { color: t.green } }}
                    />
                  }
                  label={
                    <Box>
                      <Box sx={{ fontSize: "13px", color: t.text }}>{setting.label}</Box>
                      <Box sx={{ fontSize: "11px", color: t.t3 }}>{setting.hint}</Box>
                    </Box>
                  }
                />
              ))}
            </Box>
          </Box>
        </>
      )}
    </StepShell>
  );
};

export default WorkoutStep;
